/**
 * Progressive quoting, the client side: reading the API's quote stream (`POST .../quotes/stream`, text/event-stream)
 * and the little bookkeeping both apps need while rates trickle in (carrier progress, the one-line summary).
 * Presentation-free; the portal and the console own fetch, state and rendering.
 */

export interface SseEvent {
  event: string;
  data: string;
}

/**
 * Parses an SSE byte stream into events (spec: `event:` / `data:` lines, blank line terminates; `:` comments and
 * other fields are ignored; multi-line data joins with "\n"). Resolves when the body ends; rejects on a read error.
 * A server that closes mid-event just drops that partial event.
 */
export async function readSse(body: ReadableStream<Uint8Array>, onEvent: (e: SseEvent) => void): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let event = "message";
  let data: string[] = [];
  const flush = (): void => {
    if (data.length) onEvent({ event, data: data.join("\n") });
    event = "message";
    data = [];
  };
  const take = (line: string): void => {
    if (line === "") return flush();
    if (line.startsWith(":")) return;
    const i = line.indexOf(":");
    const field = i < 0 ? line : line.slice(0, i);
    let value = i < 0 ? "" : line.slice(i + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value || "message";
    else if (field === "data") data.push(value);
  };
  try {
    for (;;) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      let nl: number;
      while ((nl = buffer.search(/\r\n|\n|\r/)) >= 0) {
        // A trailing "\r" may be half of a "\r\n" split across chunks: wait for the next chunk to decide.
        if (buffer[nl] === "\r" && nl === buffer.length - 1 && !done) break;
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + (buffer[nl] === "\r" && buffer[nl + 1] === "\n" ? 2 : 1));
        take(line);
      }
      if (done) break;
    }
  } finally {
    reader.releaseLock();
  }
}

export type StreamCarrierStatus = "waiting" | "ok" | "empty" | "error" | "timeout" | "skipped";

/** One carrier's progress while a quote streams. */
export interface StreamCarrier {
  carrier: string;
  name: string;
  status: StreamCarrierStatus;
  optionCount: number;
  notice?: string;
}

/** The `start` event: every carrier being asked, all waiting. */
export function carriersFromStart(carriers: { carrier: string; name: string }[]): StreamCarrier[] {
  return carriers.map((c) => ({ carrier: c.carrier, name: c.name, status: "waiting", optionCount: 0 }));
}

/** A `carrier` event folded in: that carrier's status, count and notice (a carrier not in `start` is appended). */
export function applyCarrierEvent(list: StreamCarrier[], e: { carrier: string; name?: string; status: Exclude<StreamCarrierStatus, "waiting">; optionCount: number; notice?: string }): StreamCarrier[] {
  const next = { carrier: e.carrier, name: e.name ?? list.find((c) => c.carrier === e.carrier)?.name ?? e.carrier, status: e.status, optionCount: e.optionCount, ...(e.notice ? { notice: e.notice } : {}) };
  const i = list.findIndex((c) => c.carrier === e.carrier);
  return i < 0 ? [...list, next] : list.map((c, j) => (j === i ? next : c));
}

/** A streamed rate counted against its carrier while it is still waiting (so the line reads "EagleLoad 12 rates"). */
export function countStreamedRate(list: StreamCarrier[], carrier: string): StreamCarrier[] {
  return list.map((c) => (c.carrier === carrier && c.status === "waiting" ? { ...c, optionCount: c.optionCount + 1 } : c));
}

const rates = (n: number): string => `${n} rate${n === 1 ? "" : "s"}`;

/** "EagleLoad 12 rates · Old Dominion waiting · Estes no rates · XPO didn't answer". */
export function carrierProgressText(list: readonly StreamCarrier[]): string {
  return list
    .filter((c) => c.status !== "skipped")
    .map((c) => {
      switch (c.status) {
        case "waiting":
          return c.optionCount > 0 ? `${c.name} ${rates(c.optionCount)}…` : `${c.name} waiting`;
        case "ok":
          return `${c.name} ${rates(c.optionCount)}`;
        case "empty":
          return `${c.name} no rates`;
        case "timeout":
          return `${c.name} timed out`;
        default:
          return `${c.name} didn't answer`;
      }
    })
    .join(" · ");
}

/** What a screen reader hears when one carrier settles (the running count line would be far too chatty). */
export function carrierDoneText(c: StreamCarrier): string | null {
  switch (c.status) {
    case "ok":
      return `${c.name} answered with ${rates(c.optionCount)}.`;
    case "empty":
      return `${c.name} has no rates for this shipment.`;
    case "timeout":
      return `${c.name} timed out.`;
    case "error":
      return `${c.name} didn't answer.`;
    default:
      return null;
  }
}

/** Insert keeping the list sorted by `rank` (stable: ties go after existing rows), without re-sorting the whole list. */
export function insertSorted<T>(list: readonly T[], item: T, rank: (x: T) => number): T[] {
  const r = rank(item);
  let i = list.length;
  while (i > 0 && rank(list[i - 1]!) > r) i--;
  return [...list.slice(0, i), item, ...list.slice(i)];
}
